import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Mail, User, X, Save, Trash2, AlertTriangle, Eye, EyeOff, Users, TrendingUp, Calendar, BarChart3, Plane, UserCheck, Palette } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { ROLES } from '../data/mockData';
import { 
  calculateCapacitesEquipe, 
  calculateCapacitesPersonne, 
  formatPeriode, 
  calculateNombreSemaines,
  generateSuggestionsProductivite
} from '../utils/capacityUtils';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter, 
  startOfYear, 
  endOfYear,
  subMonths
} from 'date-fns';
import VacancesModal from '../components/VacancesModal';
import { equipesService } from '../services/api';

const Personnes = () => {
  const navigate = useNavigate();
  const { state, getFilteredPersonnes, actions } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [personneToDelete, setPersonneToDelete] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showVacancesModal, setShowVacancesModal] = useState(false);
  const [selectedPersonneForVacances, setSelectedPersonneForVacances] = useState(null);
  const [showCreateEquipeModal, setShowCreateEquipeModal] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    role: 'developpeur',
    taux_journalier: '',
    indice_productivite: 85,
    actif: true,
    equipe_id: ''
  });
  const [equipesDisponibles, setEquipesDisponibles] = useState([]);
  const [errors, setErrors] = useState({});

  // Filter personnes based on active status
  const personnes = getFilteredPersonnes()?.filter(p => showInactive ? true : p.actif) || [];

  // Convert period selection to usable dates
  const getPeriodDates = () => {
    const { periodSelection } = state;
    
    if (periodSelection.type === 'preset') {
      switch (periodSelection.preset) {
        case 'current_month':
          return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
        case 'current_quarter':
          return { start: startOfQuarter(new Date()), end: endOfQuarter(new Date()) };
        case 'fiscal_year': {
          // Exercice en cours : du 1er juillet dernier au 30 juin prochain
          const now = new Date();
          const currentYear = now.getFullYear();
          const currentMonth = now.getMonth(); // 0-11
          
          // Si nous sommes avant le 1er juillet (mois 6), l'exercice a commencé l'année dernière
          if (currentMonth < 6) {
            const start = new Date(currentYear - 1, 6, 1); // 1er juillet année dernière
            const end = new Date(currentYear, 5, 30); // 30 juin année courante
            return { start, end };
          } else {
            // Si nous sommes le 1er juillet ou après, l'exercice a commencé cette année
            const start = new Date(currentYear, 6, 1); // 1er juillet année courante
            const end = new Date(currentYear + 1, 5, 30); // 30 juin année prochaine
            return { start, end };
          }
        }
        case '6_months':
          return { start: startOfMonth(new Date()), end: endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() + 5, new Date().getDate())) };
        default:
          return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
      }
    } else if (periodSelection.type === 'custom') {
      return {
        start: periodSelection.startDate ? new Date(periodSelection.startDate) : startOfMonth(new Date()),
        end: periodSelection.endDate ? new Date(periodSelection.endDate) : endOfMonth(new Date())
      };
    } else if (periodSelection.type === 'single') {
      const date = periodSelection.singleDate ? new Date(periodSelection.singleDate) : new Date();
      return { start: date, end: date };
    }
    
    // Default fallback
    return { start: startOfMonth(new Date()), end: endOfMonth(new Date()) };
  };

  const selectedPeriod = getPeriodDates();

  // Calcul des capacités de l'équipe
  const capacitesEquipe = calculateCapacitesEquipe(
    getFilteredPersonnes() || [], 
    selectedPeriod.start, 
    selectedPeriod.end
  );

  const suggestions = generateSuggestionsProductivite(capacitesEquipe);
  const nombreSemaines = calculateNombreSemaines(selectedPeriod.start, selectedPeriod.end);

  useEffect(() => {
    console.log('Capacités équipe:', capacitesEquipe);
    console.log('Données personnes avec indices de productivité:', 
      getFilteredPersonnes()?.map(p => ({ 
        nom: p.nom, 
        prenom: p.prenom, 
        indice_productivite: p.indice_productivite 
      }))
    );
  }, [capacitesEquipe, getFilteredPersonnes]);

  // Charger les équipes disponibles quand le modal s'ouvre
  useEffect(() => {
    if (showModal) {
      loadEquipesDisponibles();
    }
  }, [showModal]);

  const loadEquipesDisponibles = async () => {
    try {
      const response = await equipesService.getMesEquipes();
      if (response.success) {
        setEquipesDisponibles(response.data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des équipes:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    
    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format d\'email invalide';
    }
    
    if (!formData.taux_journalier || parseFloat(formData.taux_journalier) <= 0) {
      newErrors.taux_journalier = 'Le taux journalier doit être supérieur à 0';
    }
    
    if (!formData.indice_productivite || parseFloat(formData.indice_productivite) < 10 || parseFloat(formData.indice_productivite) > 100) {
      newErrors.indice_productivite = 'L\'indice de productivité doit être entre 10% et 100%';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const personneData = {
        ...formData,
        taux_journalier: parseFloat(formData.taux_journalier),
        indice_productivite: parseFloat(formData.indice_productivite)
      };
      
      await actions.createPersonne(personneData);
      
      // Reset form and close modal
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        role: 'developpeur',
        taux_journalier: '',
        indice_productivite: 85,
        actif: true,
        equipe_id: ''
      });
      setErrors({});
      setShowModal(false);
    } catch (error) {
      console.error('Erreur lors de la création de la personne:', error);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      role: 'developpeur',
      taux_journalier: '',
      indice_productivite: 85,
      actif: true,
      equipe_id: ''
    });
    setErrors({});
  };

  const handleDeleteClick = (personne) => {
    setPersonneToDelete(personne);
    setShowConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!personneToDelete) return;

    try {
      await actions.deletePersonne(personneToDelete.id);
      setShowConfirmDelete(false);
      setPersonneToDelete(null);
    } catch (error) {
      console.error('Erreur lors de la désactivation de la personne:', error);
    }
  };

  const closeConfirmDelete = () => {
    setShowConfirmDelete(false);
    setPersonneToDelete(null);
  };

  const handleReactivatePersonne = async (personneId) => {
    try {
      await actions.reactivatePersonne(personneId);
    } catch (error) {
      console.error('Erreur lors de la réactivation de la personne:', error);
    }
  };

  const handleVacancesClick = (personne) => {
    setSelectedPersonneForVacances(personne);
    setShowVacancesModal(true);
  };

  const handleCloseVacancesModal = () => {
    setShowVacancesModal(false);
    setSelectedPersonneForVacances(null);
  };

  const handleCreerEquipe = async (equipeData) => {
    try {
      await equipesService.create(equipeData);
      setShowCreateEquipeModal(false);
      // Recharger les données pour voir la nouvelle équipe
      await actions.loadAllPersonnes();
    } catch (err) {
      console.error('Erreur lors de la création de l\'équipe:', err);
      alert('Erreur lors de la création de l\'équipe');
    }
  };

  const getEquipesPersonne = (personne) => {
    if (!personne?.utilisateur?.appartenances_equipes) return [];
    return personne.utilisateur.appartenances_equipes.map(appartenance => ({
      ...appartenance.equipe,
      role: appartenance.role
    }));
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Personnes</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérez votre équipe et les collaborateurs
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button 
            onClick={() => setShowInactive(!showInactive)}
            className={`btn-secondary ${showInactive ? 'bg-gray-200' : ''}`}
          >
            {showInactive ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            {showInactive ? 'Masquer inactives' : 'Voir inactives'}
          </button>
          <button 
            onClick={() => setShowCreateEquipeModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <Users className="h-4 w-4" />
            <span>Nouvelle équipe</span>
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle personne
          </button>
        </div>
      </div>

      {/* Section des statistiques de capacité */}
      <div className="mt-8 space-y-6">
        {/* En-tête de la section capacité */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-primary-600" />
                Capacité de l'équipe
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Calculs basés sur les indices de productivité de l'API • Congés annuels (25j) déduits • Période sélectionnée via la navbar
              </p>
              <div className="mt-2 flex items-center space-x-4 text-xs">
                <div className="flex items-center">
                  <span className="text-green-600 mr-1">✓</span>
                  <span className="text-gray-600">Valeur API</span>
                </div>
                <div className="flex items-center">
                  <span className="text-yellow-600 mr-1">⚠</span>
                  <span className="text-gray-600">Valeur par défaut (85%)</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Période : </span>
                {formatPeriode(selectedPeriod.start, selectedPeriod.end)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {nombreSemaines} semaine{nombreSemaines > 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques de capacité */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Nombre de personnes */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Personnes actives
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {capacitesEquipe.nombrePersonnes}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          {/* Capacité brute totale */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Capacité brute
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {capacitesEquipe.totalCapaciteBrute?.toFixed(1) || 0} jours
                  </dd>
                  <dd className="text-xs text-gray-500">
                    {nombreSemaines} semaines × 5j - congés (25j/an)
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          {/* Capacité nette totale */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Capacité nette
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {capacitesEquipe.totalCapaciteNette?.toFixed(1) || 0} jours
                  </dd>
                  <dd className="text-xs text-gray-500">
                    Avec productivité moyenne
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          {/* Productivité moyenne */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                  capacitesEquipe.moyenneIndiceProductivite >= 85 ? 'bg-green-100' :
                  capacitesEquipe.moyenneIndiceProductivite >= 70 ? 'bg-yellow-100' : 'bg-red-100'
                }`}>
                  <TrendingUp className={`h-5 w-5 ${
                    capacitesEquipe.moyenneIndiceProductivite >= 85 ? 'text-green-600' :
                    capacitesEquipe.moyenneIndiceProductivite >= 70 ? 'text-yellow-600' : 'text-red-600'
                  }`} />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate flex items-center">
                    Productivité moyenne
                    {(() => {
                      const personnesAvecAPI = capacitesEquipe.details?.filter(p => p.indice_productivite !== undefined && p.indice_productivite !== 85).length || 0;
                      const totalPersonnes = capacitesEquipe.nombrePersonnes;
                      return personnesAvecAPI === totalPersonnes ? (
                        <span className="ml-1 text-green-600" title="Toutes les valeurs proviennent de l'API">✓</span>
                      ) : personnesAvecAPI > 0 ? (
                        <span className="ml-1 text-yellow-600" title={`${personnesAvecAPI}/${totalPersonnes} valeurs de l'API`}>⚠</span>
                      ) : (
                        <span className="ml-1 text-red-600" title="Aucune valeur de l'API, toutes par défaut">⚠</span>
                      );
                    })()}
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {capacitesEquipe.moyenneIndiceProductivite?.toFixed(0) || 0}%
                  </dd>
                  <dd className="text-xs text-gray-500">
                    Calculé depuis les données API
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions d'amélioration */}
        {suggestions.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">
                  Suggestions d'amélioration
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <ul className="list-disc list-inside space-y-1">
                    {suggestions.map((suggestion, index) => (
                      <li key={index}>
                        <strong>{suggestion.message}</strong> - {suggestion.action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tableau des personnes */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Liste des personnes ({personnes?.length || 0})
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Gestion des membres de votre équipe et leurs équipes
          </p>
        </div>
        
        {personnes?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Personne
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Équipes
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Productivité
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capacité période
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taux/jour
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {personnes.map((personne) => {
                  const equipes = getEquipesPersonne(personne);
                  const capacites = personne.actif ? calculateCapacitesPersonne(personne, selectedPeriod.start, selectedPeriod.end) : null;
                  
                  return (
                    <tr key={personne.id} className={!personne.actif ? 'opacity-60 bg-gray-50' : 'hover:bg-gray-50'}>
                      {/* Personne */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className={`h-10 w-10 ${personne.actif ? 'bg-primary-100' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                            <User className={`h-5 w-5 ${personne.actif ? 'text-primary-600' : 'text-gray-400'}`} />
                          </div>
                          <div className="ml-4">
                            <div className={`text-sm font-medium ${personne.actif ? 'text-gray-900' : 'text-gray-500'}`}>
                              {personne.prenom} {personne.nom}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="h-3 w-3 mr-1" />
                              {personne.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Rôle */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 capitalize">
                          {personne.role.replace('_', ' ')}
                        </span>
                      </td>

                      {/* Équipes */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {equipes.length > 0 ? (
                            equipes.map((equipe, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                style={{ 
                                  backgroundColor: `${equipe.couleur}20`,
                                  color: equipe.couleur 
                                }}
                              >
                                {equipe.nom}
                                {equipe.role && (
                                  <span className="ml-1 text-xs opacity-75">
                                    ({equipe.role.nom})
                                  </span>
                                )}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400">Aucune équipe</span>
                          )}
                        </div>
                      </td>

                      {/* Productivité */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div 
                              className={`h-2 rounded-full ${
                                (personne.indice_productivite * 100 ?? 85) >= 74 ? 'bg-green-500' :
                                (personne.indice_productivite * 100 ?? 85) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${personne.indice_productivite * 100 ?? 85}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {personne.indice_productivite * 100 ?? 85}%
                          </span>
                          {personne.indice_productivite !== undefined ? (
                            <span className="ml-1 text-xs text-green-600" title="Valeur provenant de l'API">
                              ✓
                            </span>
                          ) : (
                            <span className="ml-1 text-xs text-yellow-600" title="Valeur par défaut">
                              ⚠
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Capacité */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {capacites ? (
                          <div className="text-sm text-gray-900">
                            <div>Brute: {capacites.capaciteBrute.toFixed(1)}j</div>
                            <div>Nette: {capacites.capaciteNette.toFixed(1)}j</div>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">N/A</span>
                        )}
                      </td>

                      {/* Taux journalier */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${personne.actif ? 'text-gray-900' : 'text-gray-500'}`}>
                          {personne.taux_journalier} €
                        </span>
                      </td>

                      {/* Statut */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          personne.actif 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {personne.actif ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {personne.actif && (
                            <button
                              onClick={() => handleVacancesClick(personne)}
                              className="text-orange-600 hover:text-orange-900"
                              title="Gérer les vacances"
                            >
                              <Plane className="h-4 w-4" />
                            </button>
                          )}
                          <Link
                            to={`/personnes/${personne.id}`}
                            className={`${personne.actif ? 'text-primary-600 hover:text-primary-900' : 'text-gray-400'}`}
                            title="Voir détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>
                          {personne.actif ? (
                            <button
                              onClick={() => handleDeleteClick(personne)}
                              className="text-red-600 hover:text-red-900"
                              title="Désactiver la personne"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivatePersonne(personne.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Réactiver la personne"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune personne</h3>
            <p className="mt-1 text-sm text-gray-500">
              {showInactive ? 'Aucune personne inactive trouvée.' : 'Commencez par ajouter une nouvelle personne.'}
            </p>
          </div>
        )}
      </div>


      {/* Modal pour créer une nouvelle personne */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Nouvelle personne</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Prénom */}
                <div>
                  <label htmlFor="prenom" className="form-label">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    className={`form-input ${errors.prenom ? 'border-red-500' : ''}`}
                    placeholder="Jean"
                  />
                  {errors.prenom && (
                    <p className="mt-1 text-sm text-red-600">{errors.prenom}</p>
                  )}
                </div>

                {/* Nom */}
                <div>
                  <label htmlFor="nom" className="form-label">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className={`form-input ${errors.nom ? 'border-red-500' : ''}`}
                    placeholder="Dupont"
                  />
                  {errors.nom && (
                    <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
                  )}
                </div>

                {/* Email */}
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="form-label">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="jean.dupont@example.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>

                {/* Rôle */}
                <div>
                  <label htmlFor="role" className="form-label">
                    Rôle
                  </label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    {ROLES.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Équipe */}
                <div>
                  <label htmlFor="equipe_id" className="form-label">
                    Équipe (optionnel)
                  </label>
                  <select
                    id="equipe_id"
                    name="equipe_id"
                    value={formData.equipe_id}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Aucune équipe</option>
                    {equipesDisponibles.map((equipe) => (
                      <option key={equipe.id} value={equipe.id}>
                        {equipe.nom}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    La personne peut être ajoutée à une équipe maintenant ou plus tard
                  </p>
                </div>

                {/* Taux journalier */}
                <div>
                  <label htmlFor="taux_journalier" className="form-label">
                    Taux journalier (€) *
                  </label>
                  <input
                    type="number"
                    id="taux_journalier"
                    name="taux_journalier"
                    value={formData.taux_journalier}
                    onChange={handleInputChange}
                    className={`form-input ${errors.taux_journalier ? 'border-red-500' : ''}`}
                    placeholder="500"
                    min="0"
                    step="0.01"
                  />
                  {errors.taux_journalier && (
                    <p className="mt-1 text-sm text-red-600">{errors.taux_journalier}</p>
                  )}
                </div>

                {/* Indice de productivité */}
                <div>
                  <label htmlFor="indice_productivite" className="form-label">
                    Indice de productivité (%) *
                  </label>
                  <input
                    type="number"
                    id="indice_productivite"
                    name="indice_productivite"
                    value={formData.indice_productivite}
                    onChange={handleInputChange}
                    className={`form-input ${errors.indice_productivite ? 'border-red-500' : ''}`}
                    placeholder="85"
                    min="10"
                    max="100"
                    step="1"
                  />
                  {errors.indice_productivite && (
                    <p className="mt-1 text-sm text-red-600">{errors.indice_productivite}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Pourcentage de productivité par rapport à la capacité brute (10% - 100%)
                  </p>
                </div>

                {/* Statut actif */}
                <div className="sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="actif"
                      name="actif"
                      checked={formData.actif}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="actif" className="ml-2 text-sm text-gray-700">
                      Personne active
                    </label>
                  </div>
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={state.loading}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {state.loading ? 'Création...' : 'Créer la personne'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showConfirmDelete && personneToDelete && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Confirmer la désactivation</h2>
              <button
                onClick={closeConfirmDelete}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-12 w-12 text-yellow-400" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Désactiver {personneToDelete.prenom} {personneToDelete.nom}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Cette action va désactiver la personne. Elle ne sera plus visible dans les listes actives 
                    mais ses données seront conservées.
                  </p>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">
                      Attention
                    </h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      La personne ne pourra plus être assignée à de nouveaux projets, 
                      mais ses temps déjà saisis seront conservés.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeConfirmDelete}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  disabled={state.loading}
                >
                  {state.loading ? 'Désactivation...' : 'Désactiver'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de gestion des vacances */}
      <VacancesModal
        isOpen={showVacancesModal}
        onClose={handleCloseVacancesModal}
        personne={selectedPersonneForVacances}
        onVacancesUpdated={() => {
          // Recharger les données des personnes si nécessaire
          actions.loadAllPersonnes();
        }}
      />

      {/* Modal de création d'équipe */}
      {showCreateEquipeModal && (
        <CreateEquipeModal
          onClose={() => setShowCreateEquipeModal(false)}
          onSave={handleCreerEquipe}
        />
      )}
    </div>
  );
};

// Composant Modal de création d'équipe (copié de GestionEquipe.js)
const CreateEquipeModal = ({ onClose, onSave }) => {
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [couleur, setCouleur] = useState('#3b82f6');
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [devise, setDevise] = useState('EUR');
  const [langue, setLangue] = useState('fr');
  const [isLoading, setIsLoading] = useState(false);

  const couleursPredefinies = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'
  ];

  const timezones = [
    { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
    { value: 'Europe/London', label: 'Londres (UTC+0)' },
    { value: 'America/New_York', label: 'New York (UTC-5)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' }
  ];

  const devises = [
    { value: 'EUR', label: '€ Euro' },
    { value: 'USD', label: '$ Dollar US' },
    { value: 'GBP', label: '£ Livre Sterling' },
    { value: 'CAD', label: '$ Dollar Canadien' }
  ];

  const langues = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'de', label: 'Deutsch' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nom.trim()) {
      alert('Le nom de l\'équipe est obligatoire');
      return;
    }

    setIsLoading(true);
    
    try {
      await onSave({
        nom: nom.trim(),
        description: description.trim() || undefined,
        couleur,
        timezone,
        devise,
        langue
      });
    } catch (error) {
      console.error('Erreur lors de la création:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center space-x-2">
            <UserCheck className="w-6 h-6 text-blue-600" />
            <span>Créer une nouvelle équipe</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations de base */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Informations de base</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'équipe *
              </label>
              <input
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Équipe Marketing"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (optionnel)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Décrivez brièvement votre équipe..."
              />
            </div>
          </div>

          {/* Apparence */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide flex items-center">
              <Palette className="w-4 h-4 mr-2" />
              Apparence
            </h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Couleur de l'équipe
              </label>
              <div className="flex space-x-2">
                {couleursPredefinies.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setCouleur(color)}
                    className={`w-10 h-10 rounded-lg border-2 transition-all ${
                      couleur === color ? 'border-gray-800 scale-110' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Paramètres */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 uppercase tracking-wide">Paramètres régionaux</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuseau horaire
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {timezones.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Devise
                </label>
                <select
                  value={devise}
                  onChange={(e) => setDevise(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {devises.map((currency) => (
                    <option key={currency.value} value={currency.value}>{currency.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue
              </label>
              <select
                value={langue}
                onChange={(e) => setLangue(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {langues.map((lang) => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !nom.trim()}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-all"
            >
              {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
              <span>{isLoading ? 'Création...' : 'Créer l\'équipe'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Personnes; 